import { isProfileCoreId } from '../intake.js';
import { HEADSHOT_FIELD_ID, NAME_PART_KEYS, coreColumnOf } from './keys.js';
import { CLIENT_TALENT_COLUMNS, } from './types.js';
import { canonicalJson, isEmptyAnswer } from './values.js';
/** Stores that serve themselves (their own read paths), never through the projection. */
export const SELF_SERVED = ['vault', 'secure', 'photo', 'document'];
/** A key of this storage has its own read and write path (vault, secure, photos, documents): never an answer here. */
export const isSelfServed = (storage) => SELF_SERVED.includes(storage);
/**
 * A key of this storage is never derived into the answer tables nor projected: the row's own columns
 * (`core`) and the self-served stores. A legacy value under such a key stays where it is (residue, spec 3.4).
 */
export const isResidueStorage = (storage) => storage === 'core' || SELF_SERVED.includes(storage);
/**
 * The talent value of a key may be served to a row (spec 3.6 rows 4 and 5): an active grant of the row's
 * own record, or an implied key of a linked row the talent has not left whose value is not provisional.
 */
export const servable = (i) => i.granted || (i.implied && !i.provisional);
/**
 * THE precedence (spec 3.6). The SQL talent_resolved_value (0094) implements rows 2, 4, 5 and 6 of the
 * same truth table; a PG test (test/sql/0094-schema.test.mjs) runs the table (RESOLVE_CASES in
 * test/fixtures/answer-truth-tables.mjs) through both.
 *   1. storage vault, secure, photo or document: none.
 *   2. an agency value exists: it (a null value is the agency's blank).
 *   3. owner agency or per_agency: none.
 *   4. granted and a talent value exists: the talent value.
 *   5. implied and a talent value that is not provisional: the talent value.
 *   6. none.
 */
export function resolveOne(i) {
    const none = { value: undefined, from: 'none' };
    if (SELF_SERVED.includes(i.storage))
        return none;
    if (i.agency)
        return { value: i.agency.value, from: 'agency' };
    if (i.owner !== 'talent')
        return none;
    if (i.talent && servable({ granted: i.granted, implied: i.implied, provisional: i.talent.provisional }))
        return { value: i.talent.value, from: 'talent' };
    return none;
}
/** Own data property, whatever the key (a stored field id can never reach a prototype). */
function setOwn(o, k, v) {
    Object.defineProperty(o, k, { value: v, enumerable: true, writable: true, configurable: true });
}
/**
 * Monotone merge: sets projected values, never deletes a profile key and never clears a column. Empty
 * values in `next` (isEmptyAnswer, or a blank full_name) are ignored, so a blank can never overwrite.
 */
export function mergeProjection(base, next) {
    const profile = {};
    for (const [k, v] of Object.entries(base.profile ?? {}))
        setOwn(profile, k, v);
    for (const [k, v] of Object.entries(next.profile ?? {}))
        if (!isEmptyAnswer(v))
            setOwn(profile, k, v);
    const pick = (n, b) => (n === undefined || isEmptyAnswer(n) ? b : n);
    return {
        full_name: typeof next.full_name === 'string' && next.full_name.trim() ? next.full_name : base.full_name,
        email: pick(next.email, base.email),
        phone: pick(next.phone, base.phone),
        extra_phones: pick(next.extra_phones, base.extra_phones),
        headshot_url: pick(next.headshot_url, base.headshot_url),
        profile,
    };
}
/** Canonical JSON with an empty value read as absent. */
const comparable = (v) => (isEmptyAnswer(v) ? 'null' : canonicalJson(v));
/** Compares canonical JSON after dropping empty values (isEmptyAnswer) on both sides. Columns in their fixed
 *  order, then profile field ids sorted. */
export function diffProjection(a, b) {
    const diffs = [];
    for (const column of CLIENT_TALENT_COLUMNS) {
        if (column === 'profile')
            continue;
        if (comparable(a[column]) !== comparable(b[column]))
            diffs.push({ column });
    }
    const pa = a.profile ?? {};
    const pb = b.profile ?? {};
    for (const field_id of [...new Set([...Object.keys(pa), ...Object.keys(pb)])].sort()) {
        if (comparable(pa[field_id]) !== comparable(pb[field_id]))
            diffs.push({ column: 'profile', field_id });
    }
    return diffs;
}
const NAME_PARTS = Object.values(NAME_PART_KEYS);
/** A primary headshot value (CORE_COLUMN_KEYS.headshot_url): the photo's url plus the agency's crop metadata.
 *  Each part is projected when present (a legacy row can hold the metadata without the url, or the reverse). */
function headshotOf(v) {
    if (!v || typeof v !== 'object' || Array.isArray(v))
        return null;
    const { url, meta } = v;
    return { ...(typeof url === 'string' && url.trim() ? { url } : {}), ...(isEmptyAnswer(meta) ? {} : { meta }) };
}
/**
 * The projection rules that need no registry (spec 3.6 projectionFor), applied monotonically onto `base`
 * (the row's current columns for writes, EMPTY_COLUMNS for the parity gates):
 * - the primary headshot (CORE_COLUMN_KEYS.headshot_url) {url, meta}: headshot_url = url; profile.__headshot = meta when meta is set;
 * - core keys: full_name (a non-empty string only), email, phone, extra_phones;
 * - name parts are never projected on their own;
 * - storage `answer`: under each of the agency's field ids, else the fallback id;
 * - storage `performer`: only under ids the agency itself has (its person-level home is projectPerformer);
 * - every other storage (vault, secure, photo, document) serves itself: never projected;
 * - a PROFILE_CORE_IDS id (first_name, last_name, email, phone) is NEVER written into profile. Such ids
 *   already in `base` (pre Phase 0 residue) are kept untouched, never added to.
 */
export function projectFields(fields, base) {
    const next = { profile: {} };
    for (const f of fields) {
        if (isEmptyAnswer(f.value) || NAME_PARTS.includes(f.key))
            continue;
        const column = coreColumnOf(f.key);
        if (column === 'headshot_url') {
            const shot = headshotOf(f.value);
            if (shot?.url)
                next.headshot_url = shot.url;
            if (shot && 'meta' in shot)
                setOwn(next.profile, HEADSHOT_FIELD_ID, shot.meta);
            continue;
        }
        if (column === 'extra_phones') {
            if (Array.isArray(f.value))
                next.extra_phones = f.value;
            continue;
        }
        if (column) {
            if (typeof f.value === 'string')
                next[column] = f.value;
            continue;
        }
        if (f.storage !== 'answer' && f.storage !== 'performer')
            continue;
        const ids = f.fieldIds.length ? f.fieldIds : f.storage === 'answer' && f.fallbackId ? [f.fallbackId] : [];
        for (const id of ids)
            if (!isProfileCoreId(id))
                setOwn(next.profile, id, f.value);
    }
    return mergeProjection(base, next);
}
//# sourceMappingURL=visibility.js.map